import { api, type AssessmentTeamRef } from "./api";

export type SetTeamAssessmentsFn = (
  teamId: string,
  ids: string[],
) => Promise<AssessmentTeamRef[]>;

export interface SetTeamAssessmentsWithRetryOptions {
  delaysMs?: number[];
  sleep?: (ms: number) => Promise<void>;
  call?: SetTeamAssessmentsFn;
}

// Auto-retry the assessments PUT a couple of times on likely-transient
// failures (network blip / 5xx) before surfacing the sticky error from
// task #34. Permanent client errors (400/403/404/409/422 etc.) are
// re-thrown immediately so we don't paper over real problems.
export async function setTeamAssessmentsWithRetry(
  teamId: string,
  ids: string[],
  opts: SetTeamAssessmentsWithRetryOptions = {},
): Promise<AssessmentTeamRef[]> {
  const delaysMs = opts.delaysMs ?? [300, 900];
  const sleep =
    opts.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const call = opts.call ?? api.setTeamAssessments.bind(api);
  let lastErr: unknown;
  for (let attempt = 0; attempt <= delaysMs.length; attempt += 1) {
    try {
      return await call(teamId, ids);
    } catch (err) {
      lastErr = err;
      const status = (err as { status?: number } | null)?.status;
      const transient = status === undefined || status === 0 || status >= 500;
      const delay = delaysMs[attempt];
      if (!transient || delay === undefined) throw err;
      await sleep(delay);
    }
  }
  throw lastErr;
}
